import { motion } from "framer-motion";
import React, { useState } from "react";
import { isMobile } from "react-device-detect";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom";
import useEvents from "../../hooks/useEvents";
import useHoverSfx from "../../hooks/useHoverSfx";
import { chatsSlice } from "../../modules/chat/store/chatSlice";
import { OpenArenaDialog } from "../../redux/arena-dialog/action";
import Dialog from "../Dialog";
import RewardDialogRoute from "../reward-dialog/RewardDialogRoute";
import { CustomIcon } from "../Icons";
import PlayButton from "../PlayButton";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import "./map-hud.css";

const MapBottomBar = () => {
  const { t } = useAppTranslation(["navigation", "common", "adventure", "village"]);
  const location = useLocation();
  const dispatch = useDispatch();
  const { halloweenEvent } = useEvents();
  const [isHovering, setIsHovering] = useState(false);
  const [adventureTypeDialog, setAdventureTypeDialog] = useState(false);
  const hoverSfx = useHoverSfx();
  const isHomePage =
    location.pathname === "/home" || location.pathname.startsWith("/village");

  const openArenaDialog = () => dispatch(OpenArenaDialog({ open: true }));
  const openChat = () => dispatch(chatsSlice.actions.setChatState(true));

  // latest chat message for the preview bar (conversation list is kept loaded
  // by the still-mounted ChatButton bootstrap)
  const latestMessage = useSelector((state: any) => {
    const lists = state?.chats?.conversationListByType;
    if (!lists) return null;
    const all: any[] = Object.values(lists).flat();
    const withMsg = all.filter((c) => c && c.lastMessage && c.lastMessage.message);
    if (!withMsg.length) return null;
    const ts = (c: any) =>
      new Date(
        c.lastMessage.updatedAt || c.lastMessage.createdAt || c.updatedAt || 0
      ).getTime();
    const top = withMsg.sort((a, b) => ts(b) - ts(a))[0];
    const meta = state?.chats?.usersMetaData || {};
    const senderName =
      top.type === "direct"
        ? meta[top.lastMessage.senderId]?.username || t("navigation:chat")
        : top.name || top.chatName || t("navigation:guild");
    return { senderName, text: top.lastMessage.message as string };
  });

  return (
    <>
      {/* Events dialog: #events / #wheel / #wheel-key=<id>, and #quests for its
          Quests tab (quests live there now, not in a floating map button). */}
      <RewardDialogRoute />

      <motion.div
        className="map-hud-bottom"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        initial={isMobile ? undefined : { y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.35 }}
      >
        <div className="mhb-frame">
          {!isHomePage && (
            <Link
              to="/home"
              className="mhb-home"
              aria-label={t("navigation:goToHome")}
              {...hoverSfx("bottombar", 0)}
            >
              <span className="mhb-label">{t("navigation:home")}</span>
            </Link>
          )}

          {/* Nav slots carry ascending seeds, so sweeping the bar left-to-right
              walks up the pentatonic scale instead of repeating one knock. */}
          <div className="mhb-buttons">
            <Link
              to="/ranking"
              className="mhb-btn"
              aria-label={t("navigation:ranking")}
              {...hoverSfx("bottombar", 0)}
            >
              <img className="mhb-ico mhb-ico--ranking" src="/assets/images/icons/crown.png" alt="" />
              <span className="mhb-label">{t("navigation:ranking")}</span>
            </Link>

            <Link
              to="/marketplace"
              className="mhb-btn"
              aria-label={t("navigation:market")}
              {...hoverSfx("bottombar", 1)}
            >
              <img className="mhb-ico mhb-ico--market" src="/assets/images/icons/market.png" alt="" />
              <span className="mhb-label">{t("navigation:market")}</span>
            </Link>

            {/* center chest = legacy PlayButton (arena), re-skinned via CSS */}
            <div className="mhb-play" {...hoverSfx("chest")}>
              <PlayButton isHovering={isHovering} animationSlide={false} />
            </div>

            <button
              className="mhb-btn"
              onClick={() => setAdventureTypeDialog(true)}
              aria-label={t("navigation:adventure")}
              {...hoverSfx("bottombar", 3)}
            >
              {halloweenEvent.isActive && (
                <div className="fire-badge mhb-badge">
                  {halloweenEvent.extraTreasureRewardMultiplier}X
                </div>
              )}
              <img className="mhb-ico mhb-ico--adventure" src="/assets/images/icons/skull.png" alt="" />
              <span className="mhb-label">{t("navigation:adventure")}</span>
            </button>

            <Link
              to="/barracks"
              className="mhb-btn"
              aria-label={t("navigation:army")}
              {...hoverSfx("bottombar", 4)}
            >
              <img className="mhb-ico mhb-ico--army" src="/assets/images/hud/helmet.png" alt="" />
              <span className="mhb-label">{t("navigation:army")}</span>
            </Link>
          </div>

          {/* chat preview bar — shows the latest message, opens the chat drawer */}
          <div
            className="mhb-chat"
            role="button"
            onClick={openChat}
            aria-label={t("navigation:openChat")}
            {...hoverSfx("chat")}
          >
            <img className="mhb-chat-ico" src="/assets/images/hud/msg-envelope.png" alt="" />
            <span className="mhb-chat-text">
              {latestMessage
                ? `${latestMessage.senderName}: ${latestMessage.text}`
                : t("navigation:openChat")}
            </span>

          </div>
        </div>
      </motion.div>

      {/* ---- Adventure Type dialog (preserved from the legacy bottom bar) ---- */}
      <Dialog
        closeLabel={t("common:dialogs.notNow")}
        noFooter
        onClose={() => setAdventureTypeDialog(false)}
        open={adventureTypeDialog}
        title={t("adventure:types.title")}
      >
        <div
          onClick={() => setAdventureTypeDialog(false)}
          className="row align-items-center text-center"
        >
          <div className="col-6 pointer adventure-type">
            <Link to={"/dungeon/select"}>
              <motion.img
                transition={{ duration: 0.4, delay: 0.1 }}
                initial={isMobile ? undefined : { y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 250, opacity: 0 }}
                alt={t("village:buildings.12.name")}
                src={"/assets/images/adventure-icons/dungeon.png"}
                style={{ maxHeight: 167 }}
                width={"100%"}
              />
              <motion.p className="dark-yellow d-flex align-items-center justify-content-center mt-4 bold">
                {halloweenEvent.extraRewardTreasureId <= 8 &&
                  halloweenEvent.extraRewardTreasureId !== 0 &&
                  halloweenEvent.extraTreasureRewardMultiplier > 1 && (
                    <div
                      style={{ position: "relative", marginTop: -8 }}
                      className="fire-badge"
                    >
                      {halloweenEvent.extraTreasureRewardMultiplier}X
                    </div>
                  )}{" "}
                {t("village:buildings.12.name")}
              </motion.p>
            </Link>
          </div>

          <div className="col-6 p-relative pointer adventure-type">
            <Link to={"/treasure/select"}>
              <motion.img
                transition={{ duration: 0.4, delay: 0.2 }}
                initial={isMobile ? undefined : { y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 250, opacity: 0 }}
                alt={t("adventure:dungeon.labels.treasureIsland")}
                src={"/assets/images/adventure-icons/treasure.png"}
                width={"100%"}
                style={{ maxHeight: 167 }}
              />
              <motion.p className="dark-yellow mt-4 bold justify-content-center d-flex align-items-center">
                {halloweenEvent.extraRewardTreasureId > 8 &&
                  halloweenEvent.extraRewardTreasureId !== 0 &&
                  halloweenEvent.extraTreasureRewardMultiplier > 1 && (
                    <div
                      className="fire-badge"
                      style={{ position: "relative", marginTop: -8 }}
                    >
                      {halloweenEvent.extraTreasureRewardMultiplier}X
                    </div>
                  )}{" "}
                {t("adventure:dungeon.labels.treasureIsland")}
              </motion.p>
            </Link>
          </div>

          <div className="col-6 p-relative pointer adventure-type">
            <Link to={"/guild"}>
              <motion.img
                transition={{ duration: 0.4, delay: 0.2 }}
                initial={isMobile ? undefined : { y: 100, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 250, opacity: 0 }}
                alt={t("navigation:guild")}
                src={"/assets/images/adventure-icons/castle.png"}
                width={"100%"}
                style={{ maxHeight: 167 }}
              />
              <motion.p className="dark-yellow mt-4 bold justify-content-center d-flex align-items-center">
                {t("navigation:guild")}
              </motion.p>
            </Link>
          </div>

          <div className="col-12 text-center pt-3" style={{ marginBottom: -50 }}>
            <Link
              onClick={() => setAdventureTypeDialog(false)}
              to="/adventure"
              className="dark-yellow d-flex justify-content-center gap-2 align-items-center"
            >
              <CustomIcon width={25} height={25} icon="history" type="svg" />
              {t("adventure:titles.history")}
            </Link>
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default MapBottomBar;
